import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { errorMessage } from '../api/client';
import { useAuthConfig, useAuthMutations } from '../api/hooks';
import { AuthFrame } from '../components/AuthFrame';
import { Field } from '../components/Field';
import { de } from '../i18n/de';

/**
 * Registrierung für die Beta, ohne Bestätigung per E-Mail. Je nach Einstellung des Servers offen, mit Einladungscode oder
 * geschlossen. Das Passwort wird zweimal eingegeben: Ohne Mail-Dienst gibt es noch kein „Passwort vergessen“.
 */
export function RegisterPage() {
  const { register } = useAuthMutations();
  const { data: cfg, isLoading } = useAuthConfig();
  const [params] = useSearchParams();
  const next = params.get('next');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const mode = cfg?.registration;
  const loginLink = next ? `/login?next=${encodeURIComponent(next)}` : '/login';

  if (isLoading) return <div className="page muted">{de.common.loading}</div>;
  if (mode === 'closed') {
    return (
      <AuthFrame title={de.auth.registerTitle}>
        <div className="notice" data-testid="register-closed">
          {de.auth.closed}
        </div>
        <div className="auth-links">
          <Link to={loginLink}>{de.auth.login}</Link>
        </div>
      </AuthFrame>
    );
  }

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (password !== repeat) return setMismatch(true);
    register.mutate({ email, password, ...(mode === 'invite' ? { inviteCode } : {}) });
  };

  return (
    <AuthFrame title={de.auth.registerTitle}>
      <div className="muted small">{mode === 'invite' ? de.auth.inviteHint : de.auth.registerIntro}</div>
      <form className="col auth-form" onSubmit={submit}>
        {mode === 'invite' && (
          <Field label={de.auth.inviteCode}>
            <input type="text" autoComplete="off" spellCheck={false} required value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} autoFocus data-testid="invite-code" />
          </Field>
        )}
        <Field label={de.auth.email}>
          <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus={mode !== 'invite'} />
        </Field>
        <Field label={de.auth.password} help={de.auth.passwordHint}>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setMismatch(false);
            }}
          />
        </Field>
        <Field label={de.auth.passwordRepeat}>
          <input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={repeat}
            onChange={(e) => {
              setRepeat(e.target.value);
              setMismatch(false);
            }}
          />
        </Field>
        {mismatch && (
          <div className="error-text" role="alert">
            {de.auth.passwordMismatch}
          </div>
        )}
        {register.isError && (
          <div className="error-text" role="alert">
            {errorMessage(register.error)}
          </div>
        )}
        <button className="btn primary" type="submit" disabled={register.isPending} data-testid="register-submit">
          {de.auth.register}
        </button>
      </form>
      <div className="small muted">{cfg?.passwordReset ? de.auth.betaNote : de.auth.noReset}</div>
      <div className="small muted">{de.auth.privacy}</div>
      <div className="auth-links">
        <span>
          {de.auth.haveAccount} <Link to={loginLink}>{de.auth.login}</Link>
        </span>
      </div>
    </AuthFrame>
  );
}
